import { httpBatchLink, loggerLink } from "@trpc/client"
import { createTRPCReact } from "@trpc/react-query"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import superjson from "superjson"

import { IS_LOCAL_DEV } from "../../../../packages/env"
// Type-only import for vidmax-api AppRouter
import type { AppRouter } from "../../../vidmax-api/src/router/_app"
import { isVidmaxDevToken } from "./devAuth"
import { getAPIBaseUrl, isProdVercelEnv } from "./env"

export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

/**
 * Create tRPC React hooks with proper typing
 * Keep this inferred from AppRouter: deployment type-checks depend on router moves breaking callers.
 */
const typedTrpc = createTRPCReact<AppRouter>()

export const getClientBuild = (): string | undefined => {
  if (typeof document === "undefined") return undefined

  const commitSha = document.querySelector<HTMLMetaElement>('meta[name="git-commit"]')?.content.trim()
  return commitSha && /^[a-f0-9]{7,40}$/i.test(commitSha) ? commitSha : undefined
}

export const trpc = typedTrpc

/**
 * Helper to create tRPC client for React apps (matches Next.js setup)
 * Call this in your main.tsx to setup ReactQuery + tRPC
 */
export const createTRPCReactClient: () => ReturnType<typeof trpc.createClient> = () => {
  return trpc.createClient({
    transformer: superjson,
    links: [
      loggerLink({
        enabled: (opts) => import.meta.env.DEV || (opts.direction === "down" && opts.result instanceof Error),
      }),
      httpBatchLink({
        url: `${getAPIBaseUrl()}/api/trpc`,
        async fetch(url, options) {
          // Local dev needs to be longer because local uses polling for some functionality
          const timeoutMs = IS_LOCAL_DEV ? 120000 : 5000 // 120s for local development, 5s for queries

          // Dev-only: if a `lomin*` dev token is stored (set via the "Dev Login" button
          // on /auth/signin), send it as a Bearer header so the API's dev-auth backdoor
          // (context-adapter) authenticates the browser session — same mechanism CLI
          // tools and e2e tests use. No-op in prod (gate) and when no token is stored.
          const storedDevAuthToken =
            !isProdVercelEnv && typeof window !== "undefined" ? window.localStorage.getItem("dev-auth-token") : null
          const devAuthToken = storedDevAuthToken && isVidmaxDevToken(storedDevAuthToken) ? storedDevAuthToken : null
          const devImpersonateEmail =
            devAuthToken && typeof window !== "undefined"
              ? window.localStorage.getItem("dev-impersonate-email")?.trim()
              : null
          const clientBuild = getClientBuild()
          const requestOptions = {
            ...options,
            headers: {
              ...options?.headers,
              "x-trpc-source": "vidmax-web",
              ...(clientBuild ? { "x-client-build": clientBuild } : {}),
              ...(devAuthToken ? { Authorization: `Bearer ${devAuthToken}` } : {}),
              ...(devImpersonateEmail ? { "x-dev-impersonate": devImpersonateEmail } : {}),
            },
          }

          // Retry policy for transient failures (mostly local-dev API restarts).
          // Queries (GET) are idempotent → retry on fetch-throw OR 5xx.
          // Mutations (POST) → only retry on fetch-throw (no response = request never landed); never on 5xx.
          const isMutation = (options?.method ?? "GET").toUpperCase() !== "GET"
          const maxAttempts = IS_LOCAL_DEV ? 3 : isMutation ? 1 : 2
          const backoffs = [300, 600]

          let lastErr: unknown
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
            try {
              const res = await fetch(url, {
                ...requestOptions,
                credentials: "include",
                signal: controller.signal,
              })
              clearTimeout(timeoutId)
              if (!isMutation && res.status >= 500 && attempt < maxAttempts - 1) {
                await new Promise((r) => setTimeout(r, backoffs[attempt] ?? 600))
                continue
              }
              return res
            } catch (err) {
              clearTimeout(timeoutId)
              lastErr = err
              if (attempt < maxAttempts - 1) {
                await new Promise((r) => setTimeout(r, backoffs[attempt] ?? 600))
                continue
              }
              throw err
            }
          }
          throw lastErr
        },
      }),
    ],
  })
}
